import type { VotingQuestion } from '../../api/DigiobcanApi';
import {
  FREE_TEXT_MAX_LENGTH,
  scalePoints,
  type AnswerOption,
} from '../../lib/votings';
import { __ } from '../../utils';
import { Link } from '../Link';
import { Box, Stack, TextField, Typography } from '@presov.sk/presov-ui-react';
import React from 'react';

type Props = {
  question: VotingQuestion;
  value: AnswerOption[];
  onChange: (next: AnswerOption[]) => void;
  /**
   * Občan sa už raz pokúsil pokračovať. Až vtedy sa kreslí červeno to, čo
   * chýba - dopredu by bol prázdny formulár samá chyba.
   */
  showMissing?: boolean;
};

const OPTION_ROW = {
  minHeight: { xs: 56, md: 60 },
  px: { xs: 1.75, md: 2.25 },
  py: 1.25,
};

/** Prepínač / zaškrtávacie pole nakreslené tak, ako vyzerá v návrhu. */
const Mark: React.FC<{ shape: 'radio' | 'check'; checked: boolean }> = ({
  shape,
  checked,
}) => (
  <Box
    aria-hidden
    sx={({ palette, shape: themeShape }) => ({
      flex: 'none',
      width: 22,
      height: 22,
      display: 'grid',
      placeItems: 'center',
      borderRadius:
        shape === 'radio'
          ? `${themeShape.borderRadiusFull}px`
          : `${themeShape.borderRadiusSm}px`,
      border: `2px solid ${checked ? palette.primary.main : palette.grey[400]}`,
      backgroundColor:
        checked && shape === 'check' ? palette.primary.main : 'transparent',
    })}
  >
    {checked && shape === 'radio' && (
      <Box
        sx={({ palette, shape: themeShape }) => ({
          width: 11,
          height: 11,
          borderRadius: `${themeShape.borderRadiusFull}px`,
          backgroundColor: palette.primary.main,
        })}
      />
    )}
    {checked && shape === 'check' && (
      <Box
        component='svg'
        viewBox='0 0 24 24'
        width={14}
        height={14}
        fill='none'
        stroke='#FFFFFF'
        strokeWidth={3}
        strokeLinecap='round'
        strokeLinejoin='round'
      >
        <path d='M5 13l4 4L19 7' />
      </Box>
    )}
  </Box>
);

const OptionRow: React.FC<
  React.PropsWithChildren<{
    checked: boolean;
    invalid?: boolean;
    onToggle: () => void;
  }>
> = ({ checked, invalid, onToggle, children }) => (
  <Box
    component='label'
    onClick={onToggle}
    sx={({ palette, shape }) => ({
      ...OPTION_ROW,
      display: 'flex',
      alignItems: 'center',
      gap: 1.75,
      cursor: 'pointer',
      borderRadius: `${shape.borderRadius}px`,
      border: `1px solid ${
        invalid
          ? palette.primary.main
          : checked
            ? palette.grey[900]
            : palette.divider
      }`,
      backgroundColor: checked ? palette.grey[50] : palette.background.paper,
      fontSize: 17,
      fontWeight: checked ? 500 : 400,
      '&:hover': {
        borderColor: checked ? palette.grey[900] : palette.grey[400],
      },
    })}
  >
    {children}
  </Box>
);

/**
 * Jedna otázka na celú obrazovku - to, čo v sústredenom režime stojí medzi
 * pásikom postupu a pätičkou.
 *
 * Je to plain React state, nie `react-final-form`: sústredený režim je
 * sprievodca, nie formulár. Krok sa validuje sám za seba, „Ďalej" sa odomyká
 * podľa pravidla otázky a odosiela sa až z kontroly - `final-form` by tu
 * musel byť obídený na všetkých troch miestach. Tvar hodnoty (`AnswerOption[]`
 * na `question_id`) zostáva rovnaký ako predtým, aby sa telo požiadavky
 * skladalo tou istou funkciou.
 */
const QuestionStep: React.FC<Props> = ({
  question,
  value,
  onChange,
  showMissing,
}) => {
  const safeValue = React.useMemo(
    () => (Array.isArray(value) ? value : []),
    [value],
  );

  const { question_id, type, options } = question;

  const selectedIds = React.useMemo(
    () => new Set(safeValue.map((answer) => answer.id)),
    [safeValue],
  );

  // --- Otvorená odpoveď ako celá otázka ---------------------------------
  if (type === 'free_text') {
    const freeOption = options.find((option) => option.type === 'free');
    if (!freeOption) return null;

    const current = safeValue.find((a) => a.id === freeOption.option_id);
    const text = current?.text ?? '';

    return (
      <Stack gap={1}>
        {freeOption.url && (
          <Typography variant='body2' color='text.secondary'>
            <Link to={freeOption.url} target='_blank'>
              {freeOption.url}
            </Link>
          </Typography>
        )}
        <TextField
          value={text}
          multiline
          minRows={6}
          fullWidth
          placeholder={__('Vaša odpoveď')}
          slotProps={{ htmlInput: { maxLength: FREE_TEXT_MAX_LENGTH } }}
          onChange={(event) =>
            onChange([
              {
                id: freeOption.option_id,
                questionId: question_id,
                text: event.target.value,
              },
            ])
          }
          sx={{ '& .MuiInputBase-root': { fontSize: 17, lineHeight: 1.6 } }}
        />
        <Stack direction='row' alignItems='center' gap={1.5}>
          <Typography variant='body2' color='text.secondary'>
            {__('Odpoveď je nepovinná - otázku sa dá preskočiť.')}
          </Typography>
          <Typography
            variant='body2'
            color='text.secondary'
            sx={{ ml: 'auto', flex: 'none' }}
          >
            {`${text.length} / ${FREE_TEXT_MAX_LENGTH}`}
          </Typography>
        </Stack>
      </Stack>
    );
  }

  // --- Škála -------------------------------------------------------------
  if (type === 'scale') {
    const points = scalePoints(question);
    const first = points[0];
    const last = points[points.length - 1];

    return (
      <Stack gap={2.5}>
        {options.map((option, index) => {
          const selected = safeValue.find((a) => a.id === option.option_id);
          // Nepovinné tvrdenie sa smie nechať tak, takže sa ani nezvýrazňuje
          // ako chýbajúce - inak by „chýba hodnotenie" svietilo nad niečím,
          // čo odoslaniu nebráni.
          const optional = option.required === false;
          const missing =
            showMissing && !optional && selected?.value === undefined;

          return (
            <Stack key={option.option_id} gap={1}>
              <Stack direction='row' alignItems='center' gap={1}>
                <Typography sx={{ fontSize: 17, fontWeight: 500 }}>
                  {option.title}
                </Typography>
                {optional && (
                  <Typography
                    variant='body2'
                    color='text.secondary'
                    sx={{ flex: 'none' }}
                  >
                    {__('nepovinné')}
                  </Typography>
                )}
                {missing && (
                  <Box
                    component='span'
                    sx={({ palette, shape }) => ({
                      display: 'inline-flex',
                      alignItems: 'center',
                      height: 22,
                      px: 0.875,
                      borderRadius: `${shape.borderRadiusSm}px`,
                      backgroundColor: palette.secondary.main,
                      border: `1px solid ${palette.secondary.dark}`,
                      color: palette.yellow.surfaceText,
                      fontSize: 11,
                      fontWeight: 500,
                    })}
                  >
                    {__('chýba hodnotenie')}
                  </Box>
                )}
              </Stack>

              {/* Popis tvrdenia je pri hlasovaní o projektoch celý zámer, teda
                  to, podľa čoho sa hlasujúci rozhoduje - nie doplnok. */}
              {option.description && (
                <Typography variant='body2' color='text.secondary'>
                  {option.description}
                </Typography>
              )}

              <Box
                role='radiogroup'
                aria-label={option.title}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(${points.length}, 1fr)`,
                  gap: 1,
                }}
              >
                {points.map((point) => {
                  const active = selected?.value === point;
                  return (
                    <Box
                      key={point}
                      component='button'
                      type='button'
                      role='radio'
                      aria-checked={active}
                      onClick={() =>
                        onChange([
                          ...safeValue.filter((a) => a.id !== option.option_id),
                          {
                            id: option.option_id,
                            questionId: question_id,
                            value: point,
                          },
                        ])
                      }
                      sx={({ palette, shape }) => ({
                        height: 52,
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                        fontSize: 16,
                        fontWeight: active ? 700 : 500,
                        borderRadius: `${shape.borderRadius}px`,
                        border: `1px solid ${
                          active
                            ? palette.grey[900]
                            : missing
                              ? palette.primary.main
                              : palette.divider
                        }`,
                        backgroundColor: active
                          ? palette.primary.main
                          : palette.background.paper,
                        color: active
                          ? palette.primary.contrastText
                          : palette.text.primary,
                      })}
                    >
                      {point}
                    </Box>
                  );
                })}
              </Box>

              {/* Popisy krajov len pri prvom tvrdení - pri každom by to bola
                  tá istá veta trikrát pod sebou. */}
              {index === 0 &&
                (question.scale_min_label || question.scale_max_label) && (
                  <Stack direction='row' justifyContent='space-between'>
                    <Typography variant='body2' color='text.secondary'>
                      {`${first} - ${question.scale_min_label ?? first}`}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      {`${last} - ${question.scale_max_label ?? last}`}
                    </Typography>
                  </Stack>
                )}
            </Stack>
          );
        })}
      </Stack>
    );
  }

  // --- Výber jednej alebo viacerých --------------------------------------
  const isSingle = type === 'single_choice';

  const toggle = (optionId: number) => {
    if (isSingle) {
      // Prepínač sa neodklikáva - výber jednej možnosti nemá prázdny stav
      // dosiahnuteľný klikom, len tým, že sa nič nevybralo.
      if (selectedIds.has(optionId)) return;
      onChange([{ id: optionId, questionId: question_id }]);
      return;
    }

    if (selectedIds.has(optionId)) {
      onChange(safeValue.filter((answer) => answer.id !== optionId));
      return;
    }

    onChange([...safeValue, { id: optionId, questionId: question_id }]);
  };

  return (
    <Stack gap={1.25}>
      {options.map((option) => {
        const checked = selectedIds.has(option.option_id);
        const current = safeValue.find((a) => a.id === option.option_id);

        if (option.type === 'free') {
          const missingText = showMissing && checked && !current?.text?.trim();

          return (
            <Stack
              key={option.option_id}
              gap={1.25}
              sx={({ palette, shape }) => ({
                border: `1px solid ${
                  missingText
                    ? palette.primary.main
                    : checked
                      ? palette.grey[900]
                      : palette.divider
                }`,
                borderRadius: `${shape.borderRadius}px`,
                backgroundColor: checked
                  ? palette.grey[50]
                  : palette.background.paper,
                p: 2.25,
              })}
            >
              <Box
                component='label'
                onClick={() => toggle(option.option_id)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.75,
                  cursor: 'pointer',
                  fontSize: 17,
                }}
              >
                <Mark shape={isSingle ? 'radio' : 'check'} checked={checked} />
                {__('Iné - napíšem vlastnú odpoveď')}
              </Box>

              {checked && (
                <TextField
                  autoFocus
                  fullWidth
                  multiline
                  minRows={2}
                  value={current?.text ?? ''}
                  placeholder={__('Vaša odpoveď')}
                  slotProps={{
                    htmlInput: { maxLength: FREE_TEXT_MAX_LENGTH },
                  }}
                  onChange={(event) => {
                    const text = event.target.value;
                    onChange(
                      safeValue.map((answer) =>
                        answer.id === option.option_id
                          ? { ...answer, text }
                          : answer,
                      ),
                    );
                  }}
                />
              )}
            </Stack>
          );
        }

        return (
          <OptionRow
            key={option.option_id}
            checked={checked}
            onToggle={() => toggle(option.option_id)}
          >
            <Mark shape={isSingle ? 'radio' : 'check'} checked={checked} />
            <Stack gap={0.25}>
              <span>{option.title}</span>
              {option.url && (
                <Typography
                  component={Link}
                  to={option.url}
                  target='_blank'
                  variant='body2'
                  onClick={(event: React.MouseEvent) => event.stopPropagation()}
                  sx={{ color: 'blue.dark', textDecoration: 'none' }}
                >
                  {option.url}
                </Typography>
              )}
            </Stack>
          </OptionRow>
        );
      })}
    </Stack>
  );
};

export default QuestionStep;
